import { Typography } from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { ColorDynamic, Column, Columns, Stack } from '@superdispatch/ui';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { startIntercomTour } from 'shared/helpers/Intercom';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { ChatIcon } from 'shared/icons/ChatIcon';
import { ClockIcon } from 'shared/icons/ClockIcon';
import { CompletedIcon } from 'shared/icons/CompletedIcon';
import { LoadsSidebarIcon } from 'shared/icons/LoadsSidebarIcon';
import { SuperCardIcon } from 'shared/icons/SuperCardIcon';
import { VerificationIcon } from 'shared/icons/VerificationIcon';
import { WarningRoundedIcon } from 'shared/icons/WarningRounded';
import { becomeVerifiedCarrier } from 'shared/modules/loadboard/core/BecomVerifiedCarrier';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { GET_TO_KNOW_CARRIER_TMS_TOUR_ID } from '../data/OnboardingConstants';
import { OnboardingStep } from '../data/OnboardingDTO';
import { OnboardingTaskItem } from './OnboardingTaskItem';
import { useOnboardingDialogState } from './OnboardingWrapper';

interface OnboardingVerificationTasksProps {
  onboardingSteps?: OnboardingStep;
  onClose: () => void;
}

export function OnboardingVerificationTasks({
  onboardingSteps,
  onClose,
}: OnboardingVerificationTasksProps) {
  return <CompletedSteps onboardingSteps={onboardingSteps} onClose={onClose} />;
}

interface CompletedStepsProps {
  onboardingSteps?: OnboardingStep;
  onClose: () => void;
}

function CompletedSteps({ onboardingSteps, onClose }: CompletedStepsProps) {
  const { data: settings } = useCarrierSettings();
  const { closeOnboardingDialog } = useOnboardingDialogState();
  const isSuperCardEnabled = useFlag('supercard_management');

  const verificationStatus = settings?.carrier?.verification_status;
  const isVerified = verificationStatus === 'approved';
  const isVerificationDenied =
    verificationStatus === 'declined' ||
    verificationStatus === 'non_verifiable' ||
    verificationStatus === 'revoked';
  const shouldShowCompletedVerification =
    verificationStatus && verificationStatus !== 'not_submitted';
  const steps = onboardingSteps?.steps;

  const isCreateOrImportLoadCompleted =
    steps?.tour_create_or_import_load?.status === 'started';
  const isGetToKnowCarrierTMSCompleted =
    steps?.get_to_know_ctms?.status === 'started';
  const isSLBSearchTourCompleted = steps?.tour_slb_search?.status === 'started';
  const isExploreSuperCardCompleted =
    isSuperCardEnabled && steps?.explore_supercard?.status === 'started';

  return (
    <Columns align="top" space="small">
      <Column width="content">
        <CompletedIcon />
      </Column>
      <Column width="fluid">
        <Stack space="small">
          <Typography variant="h3">Your completed steps</Typography>
          {isCreateOrImportLoadCompleted && (
            <OnboardingTaskItem
              name="Create or Import Load"
              description="Manage your loads with Carrier TMS and streamline your operations."
              icon={<LoadsSidebarIcon htmlColor={ColorDynamic.Dark100} />}
              done={isCreateOrImportLoadCompleted}
              onClick={() => {
                openExternalURL(
                  '/tms/loads?launch_create_import_loads_tour=true',
                );
                onClose();
              }}
            />
          )}
          {shouldShowCompletedVerification && (
            <OnboardingTaskItem
              done={isVerified}
              icon={
                isVerificationDenied ? (
                  <WarningRoundedIcon />
                ) : isVerified ? (
                  <VerificationIcon />
                ) : (
                  <ClockIcon />
                )
              }
              name={
                isVerificationDenied
                  ? 'Verification Documents Need Review'
                  : isVerified
                    ? 'Become Verified Carrier'
                    : 'Verification in Progress'
              }
              description={
                isVerificationDenied
                  ? 'Your Verified Carrier documents need review.'
                  : isVerified
                    ? 'Unlock unlimited and free load access. Apply now and start booking.'
                    : 'Your application has been submitted and is under review. Our team will contact you if additional information is needed. Once your application is approved you can submit load requests.'
              }
              onClick={() => {
                if (MobileAppBridge.isInjected()) {
                  MobileAppBridge.openVerifiedCarrierPromotionalModal();
                } else {
                  becomeVerifiedCarrier(verificationStatus, {
                    source: 'onboarding_dialog',
                  });
                }
                closeOnboardingDialog();
              }}
            />
          )}
          {isGetToKnowCarrierTMSCompleted && (
            <OnboardingTaskItem
              done={isGetToKnowCarrierTMSCompleted}
              name="Get to Know Carrier TMS"
              description="Take a quick tour to master Carrier TMS."
              icon={<ChatIcon />}
              onClick={() => {
                startIntercomTour(GET_TO_KNOW_CARRIER_TMS_TOUR_ID);
                onClose();
              }}
            />
          )}
          {isSLBSearchTourCompleted && (
            <OnboardingTaskItem
              done={isSLBSearchTourCompleted}
              name="Explore Super Loadboard"
              description="Access loads now! More loads unlock once you're verified."
              icon={<Search htmlColor={ColorDynamic.Dark100} />}
              onClick={() => {
                openExternalURL('/loadboard?launch_slb_search_tour=true');
                onClose();
              }}
            />
          )}
          {isExploreSuperCardCompleted && (
            <OnboardingTaskItem
              done={isExploreSuperCardCompleted}
              name="Explore SuperCard"
              description={
                <>
                  Give your drivers a fuel card that saves up to $2/gal.
                  <br />
                  No hidden fees. Integrated to driver app.
                </>
              }
              icon={<SuperCardIcon htmlColor={ColorDynamic.Dark100} />}
              onClick={() => {
                openExternalURL('/tms/super-card');
                onClose();
              }}
            />
          )}
        </Stack>
      </Column>
    </Columns>
  );
}
